import React from "react";
import { updateRequestById } from "../../data/requests";
import { requestStamp } from "../../services/requestStamp";
import { resolveDeliveryForSave } from "../../utils/helpers";
import { DeliverySelect } from "../form/ChildSections.jsx";
import { ModalBackdrop } from "../form/FormUI.jsx";

export function EditAddressModal({
  editDelivery,
  setEditDelivery,
  shelterRegAddrs,
  shelterRegAddrObjs,
  user,
  req,
  onSaveNoDb,
  showSaved,
  onClose,
}) {
  // Draggable via the existing teal header (dragHandleRef mode — no second title bar).
  const dragRef = React.useRef(null);
  return (
    <ModalBackdrop
      onClose={onClose}
      draggable
      dragHandleRef={dragRef}
      contentStyle={{
        background: "white",
        borderRadius: 12,
        width: "calc(100vw - 40px)",
        maxWidth: 560,
        boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
        // overflow stays visible so the Google Places autocomplete
        // dropdown can render below the input (it'd be clipped
        // otherwise — see CLAUDE.md loose-thread fix).
        overflow: "visible",
        fontFamily: "inherit",
      }}
    >
      <div
        ref={dragRef}
        className="bg-zephyr-teal-dark text-white py-[14px] px-5 rounded-t-[12px] flex justify-between items-center"
        style={{ cursor: "move" }}
      >
        <span className="font-bold text-base">Edit Delivery Address</span>
        <button
          onClick={onClose}
          className="bg-white/20 border border-white/40 rounded-[6px] text-white text-base font-bold cursor-pointer leading-none py-1 px-[10px]"
        >
          X
        </button>
      </div>
      <div className="p-6">
        {/* DeliverySelect is a custom picker that contains its own labelled controls,
            so this caption is a visual heading rather than a form label. */}
        <div className="block text-xs font-bold text-zephyr-muted uppercase tracking-[0.5px] mb-2">
          Delivery Address
        </div>
        <DeliverySelect
          value={editDelivery}
          onChange={function (v) {
            setEditDelivery(v);
          }}
          deliveryAddresses={
            shelterRegAddrObjs.length > 0 ? shelterRegAddrObjs : user.deliveryAddresses || []
          }
          showStatus
          locked={false}
        />
        <div className="flex gap-[10px] justify-end mt-5">
          <button
            onClick={onClose}
            className="bg-[#eee] border-none rounded-[7px] py-[9px] px-5 text-sm cursor-pointer"
            style={{ fontFamily: "inherit" }}
          >
            Cancel
          </button>
          <button
            onClick={async function () {
              if (!editDelivery?.trim()) return;
              var regStrs = shelterRegAddrs;
              var isNonReg = regStrs.length > 0 && regStrs.indexOf(editDelivery) === -1;
              // Phase 3b: also write delivery_address_id / _override.
              // Match against the request's shelter's registered addresses
              // (shelterRegAddrObjs holds the full DB rows). Fall back to
              // user's own deliveryAddresses for shelter-user edits where
              // the request's shelter == the user's shelter.
              const addrList =
                shelterRegAddrObjs.length > 0 ? shelterRegAddrObjs : user?.deliveryAddresses || [];
              const deliveryEdit = resolveDeliveryForSave(editDelivery, addrList);
              // Verified-address model: recompute the in-memory verified flag from
              // the CHOSEN address, so switching to a verified registered address
              // clears the amber "pending" warning + the approval block immediately
              // (previously it went stale until a reload). A registered pick → that
              // row's real zephyr_verified; a manual one-off (no id) → null (a flat
              // override has no linked row / no verification concept).
              const matchedAddr = deliveryEdit.id
                ? addrList.find(function (a) {
                    return a && a.id === deliveryEdit.id;
                  })
                : null;
              // matched registered row → its real verified flag (treat only an
              // explicit false as unverified); manual one-off (no match) → null.
              const deliveryVerified = matchedAddr ? matchedAddr.zephyr_verified !== false : null;
              // db-147: recompute the banned flag from the CHOSEN address too, so
              // re-routing to a non-banned address clears the red BANNED box + the
              // approval block immediately (previously it went stale). A one-off
              // (no match) is never banned → null.
              const deliveryBanned = matchedAddr ? matchedAddr.banned_address === true : null;
              var { error } = await updateRequestById(req.id, {
                delivery_address_id: deliveryEdit.id,
                delivery_address_override: deliveryEdit.override,
                non_registered_address: isNonReg,
                // Set updated_by but NOT the role, so a staff address change kept
                // whatever role was there before (23 Aug 2026).
                ...requestStamp(user),
              });
              if (!error) {
                onSaveNoDb(
                  Object.assign({}, req, {
                    deliveryAddress: editDelivery,
                    deliveryAddressId: deliveryEdit.id,
                    deliveryAddressOverride: deliveryEdit.override,
                    deliveryAddressVerified: deliveryVerified,
                    deliveryAddressBanned: deliveryBanned,
                    nonRegisteredAddress: isNonReg,
                    updatedBy: user.shelterName,
                    updatedAt: new Date().toISOString(),
                  })
                );
                onClose();
                showSaved("Delivery address updated ✓");
              }
            }}
            className="bg-zephyr-teal text-white border-none rounded-[7px] py-[9px] px-5 text-sm font-bold cursor-pointer"
            style={{ fontFamily: "inherit" }}
          >
            Save Address
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}
